import {DatePicker, Form, Input, InputNumber, Select, Switch} from 'antd';
import theme from '../ordersTheme.module.scss';
import {stockOrderStatus, walletCurrencies} from 'utils/mock';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {UserSelectField} from 'components/userSelectField/UserSelectField';
import {useEffect, useRef, useState} from 'react';
import moment from 'moment';
import {assetClassKeyTypes} from 'pages/AssetManagement/modules/constants';
import {instrumentKeyOptions} from 'pages/AssetManagement/modules/instrumentKeys';
import {reviveFilterValues} from './ordersFilterInput';
import {
  BrokerSelect,
  CollectionSelect,
  ExchangeSelect,
  InstrumentSelect,
  OrganisationSelect,
} from './filterPickers';

/**
 * The panel's six groups, and which form fields belong to each.
 *
 * It used to be 28 controls in one 500px scroller behind three bare <h3>s, so
 * finding "minimum quantity" meant reading every label on the way down. The
 * rail navigates the groups and counts what is set inside each; nothing about
 * the fields themselves — names, rules, visibility — changed.
 */
const SECTIONS = [
  {
    id: 'order',
    title: 'Order',
    fields: [
      'status',
      'direction',
      'type',
      'id',
      'txRef',
      'limitPriceSortDirection',
    ],
  },
  {
    id: 'parties',
    title: 'Parties',
    fields: ['userId', 'collectionId', 'orgId'],
  },
  {
    id: 'amounts',
    title: 'Amounts',
    fields: [
      'quantityGreaterThan',
      'quantityLessThan',
      'limitPriceGreaterThan',
      'limitPriceLessThan',
      'stopPriceGreaterThan',
      'stopPriceLessThan',
    ],
  },
  {
    id: 'venue',
    title: 'Venue & instrument',
    fields: [
      'stockExchange',
      'symbol',
      'instrumentKey',
      'assetKey',
      'instrumentId',
      'assetCurrency',
      'ipo',
      'derivative',
      'derivatives',
    ],
  },
  {
    id: 'dates',
    title: 'Dates',
    fields: [
      'startDate',
      'endDate',
      'startFillDate',
      'endFillDate',
      'startSettlementDate',
      'endSettlementDate',
      'startExpiryDate',
      'endExpiryDate',
    ],
  },
  {
    id: 'flags',
    title: 'Flags',
    fields: [
      'placed',
      'bundle',
      'assetPriceOverFloated',
      'collection',
      'affiliateOrder',
      'hold',
      'settledBySystem',
      'includePendingOrders',
    ],
  },
];

const isSet = value =>
  !(
    value === undefined ||
    value === null ||
    value === '' ||
    value === false ||
    (Array.isArray(value) && value.length === 0)
  );

const Section = ({id, title, children}) => (
  <section className={theme.filterSection} data-section={id}>
    <h3 className={theme.filterSectionTitle}>{title}</h3>
    <div className={theme.filterGrid}>{children}</div>
  </section>
);

export const OrderFilter = ({
  handleFilter,
  user,
  setUser,
  exportForm,
  activeTab,
  // The modal's own close button was a second affordance stacked on top of
  // antd's, which renders one for every DBModal.
  showStatus = false,
  isFilterModalOpen,
  reset,
  isExport,
  // What is applied right now. The panel used to open empty even when the
  // queue was filtered, so re-opening it and pressing Apply quietly cleared
  // everything the admin had set — and a restored saved view was invisible.
  appliedValues,
}) => {
  // Mirrors the form so the rail can count what is set per group. The form is
  // still the single source of truth for what gets submitted.
  const [values, setValues] = useState({});
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const panesRef = useRef(null);
  const [form] = Form.useForm();
  const currentForm = isExport ? exportForm : form;
  const instrumentKey = Form.useWatch('instrumentKey', currentForm);
  const assetKey = Form.useWatch('assetKey', currentForm);

  // Option lists live in the pickers themselves, each paging and searching on
  // the server where its query supports it. They stay unmounted until the
  // panel opens.
  const optionsSkip = !isFilterModalOpen && !isExport;

  const sortStatus = ['OPEN', 'PARTIALLY_FILLED'];

  useEffect(() => {
    form.resetFields();
    setValues({});
  }, [activeTab]);

  useEffect(() => {
    if (!isFilterModalOpen || isExport) return;
    const revived = reviveFilterValues(appliedValues ?? {});
    form.resetFields();
    form.setFieldsValue(revived);
    setValues(revived);
  }, [isFilterModalOpen, appliedValues]);

  const goToSection = id => {
    const container = panesRef.current;
    const target = container?.querySelector(`[data-section="${id}"]`);
    if (!container || !target) return;
    container.scrollTo({top: target.offsetTop - 8, behavior: 'smooth'});
    setActiveSection(id);
  };

  // Keep the rail in step with a manual scroll.
  const handlePanesScroll = () => {
    const container = panesRef.current;
    if (!container) return;
    const cutoff = container.scrollTop + 24;
    const current = SECTIONS.reduce((found, section) => {
      const target = container.querySelector(`[data-section="${section.id}"]`);
      return target && target.offsetTop <= cutoff ? section.id : found;
    }, SECTIONS[0].id);
    setActiveSection(current);
  };

  const Fields = (
    <>
      <Section id="order" title="Order">
        {!['PENDING', 'FILLED'].includes(activeTab) &&
          (!isExport || showStatus) && (
            <Form.Item
              className={theme.filterFull}
              label="Status"
              name="status">
              <Select mode={showStatus ? 'multiple' : ''} allowClear>
                {stockOrderStatus
                  .filter(data =>
                    showStatus
                      ? true
                      : activeTab === 'ACTIVE'
                      ? sortStatus.includes(data.key)
                      : !sortStatus.includes(data.key),
                  )
                  ?.map(data => (
                    <Select.Option key={data.key}>{data.name}</Select.Option>
                  ))}
              </Select>
            </Form.Item>
          )}
        <Form.Item name="direction" label="Direction">
          <Select allowClear>
            <Select.Option key="BUY">BUY</Select.Option>
            <Select.Option key="SELL">SELL</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="type" label="Order Type">
          <Select allowClear>
            <Select.Option key="LIMIT">Limit</Select.Option>
            <Select.Option key="MARKET">Market</Select.Option>
          </Select>
        </Form.Item>
        {/* An id and a transaction reference are pasted rather than picked —
            an ops person chasing one order already has the value in hand. */}
        <Form.Item name="id" label="Order ID">
          <Input allowClear placeholder="Paste an order ID" />
        </Form.Item>
        <Form.Item name="txRef" label="Transaction Reference">
          <Input allowClear placeholder="Paste a transaction reference" />
        </Form.Item>
        <Form.Item
          name="limitPriceSortDirection"
          label="Sort by Limit Price"
          tooltip="Orders the results by limit price rather than newest first.">
          <Select allowClear placeholder="Newest first">
            <Select.Option value="ASC">Lowest limit price first</Select.Option>
            <Select.Option value="DESC">
              Highest limit price first
            </Select.Option>
          </Select>
        </Form.Item>
      </Section>

      <Section id="parties" title="Parties">
        <Form.Item
          className={theme.filterFull}
          name="userId"
          label="Search & Select User">
          <UserSelectField
            users={user}
            setUsers={setUser}
            skip={!isFilterModalOpen}
            mode="single"
          />
        </Form.Item>
        {/* `brokerId` is on OrderFilter but had no control — an ops person
            could not narrow the queue to the broker they were chasing. */}
        <Form.Item name="collectionId" label="Select Collection">
          <CollectionSelect skip={optionsSkip} />
        </Form.Item>
        <Form.Item name="orgId" label="Organisation">
          <OrganisationSelect skip={optionsSkip} />
        </Form.Item>
      </Section>

      <Section id="amounts" title="Amounts">
        <Form.Item
          name="quantityGreaterThan"
          label="Minimum Quantity"
          tooltip="Filter orders with quantity greater than or equal to this value">
          <InputNumber
            style={{width: '100%'}}
            placeholder="Enter minimum quantity"
            min={0}
            className="input-number"
          />
        </Form.Item>
        <Form.Item
          name="quantityLessThan"
          label="Maximum Quantity"
          tooltip="Filter orders with quantity less than or equal to this value">
          <InputNumber
            style={{width: '100%'}}
            className="input-number"
            placeholder="Enter maximum quantity"
            min={0}
          />
        </Form.Item>
        <Form.Item
          name="limitPriceGreaterThan"
          label="Minimum Limit Price"
          tooltip="Filter orders with limit price greater than or equal to this value">
          <InputNumber
            style={{width: '100%'}}
            className="input-number"
            placeholder="Enter minimum price"
            min={0}
            step={0.01}
          />
        </Form.Item>
        <Form.Item
          name="limitPriceLessThan"
          label="Maximum Limit Price"
          tooltip="Filter orders with limit price less than or equal to this value">
          <InputNumber
            style={{width: '100%'}}
            className="input-number"
            placeholder="Enter maximum price"
            min={0}
            step={0.01}
          />
        </Form.Item>
        <Form.Item
          name="stopPriceGreaterThan"
          label="Minimum Stop Price"
          tooltip="Filter orders with stop price greater than or equal to this value">
          <InputNumber
            style={{width: '100%'}}
            className="input-number"
            placeholder="Enter minimum stop price"
            min={0}
            step={0.01}
          />
        </Form.Item>
        <Form.Item
          name="stopPriceLessThan"
          label="Maximum Stop Price"
          tooltip="Filter orders with stop price less than or equal to this value">
          <InputNumber
            style={{width: '100%'}}
            className="input-number"
            placeholder="Enter maximum stop price"
            min={0}
            step={0.01}
          />
        </Form.Item>
      </Section>

      <Section id="venue" title="Venue & instrument">
        <Form.Item
          className={theme.filterFull}
          name="stockExchange"
          label="Select Stock Exchange">
          <ExchangeSelect skip={optionsSkip} />
        </Form.Item>
        <Form.Item
          name="symbol"
          label="Symbol"
          tooltip="Matches the ticker on the order. Use the instrument picker below when you want an exact instrument.">
          <Input allowClear placeholder="e.g. AAPL" />
        </Form.Item>
        <Form.Item name="instrumentKey" label="Instrument Key">
          <Select
            placeholder="Any instrument type"
            allowClear
            options={instrumentKeyOptions([
              'PUBLIC_EQUITY',
              'BOND',
              'MUTUAL_FUND',
            ])}
          />
        </Form.Item>
        <Form.Item name="assetKey" label="Asset Class">
          <Select allowClear placeholder="Any asset class">
            {assetClassKeyTypes.map(data => (
              <Select.Option key={data.value}>{data.label}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        {instrumentKey && assetKey && (
          <Form.Item
            className={theme.filterFull}
            name="instrumentId"
            label="Instrument based on Selected Asset">
            <InstrumentSelect
              instrumentKey={instrumentKey}
              assetClass={assetKey}
              skip={optionsSkip}
            />
          </Form.Item>
        )}
        <Form.Item
          name="assetCurrency"
          label="Asset Currency"
          tooltip="The currency the instrument settles in, not the currency the user paid in.">
          <Select allowClear showSearch placeholder="Any currency">
            {walletCurrencies.map(currency => (
              <Select.Option key={currency.key} value={currency.key}>
                {currency.key} — {currency.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="derivative"
          label="Derivative"
          tooltip="A single derivative record, by id. Use Include Derivatives to widen the queue rather than pin it to one.">
          <Input allowClear placeholder="Paste a derivative ID" />
        </Form.Item>
        <div className={theme.filterSwitches}>
          <Form.Item
            name="ipo"
            label="IPO Orders"
            tooltip="Only orders placed against an IPO."
            valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="derivatives"
            label="Include Derivatives"
            tooltip="A derivative is the leftover of a partly filled order that was then rejected or expired — the unfilled units become their own record. They are excluded unless this is on."
            valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>
      </Section>

      <Section id="dates" title="Dates">
        <Form.Item name="startDate" label="Start Date">
          <DatePicker
            allowClear
            format={'YYYY-MM-DD HH:mm:ss'}
            showTime={{
              defaultValue: moment('00:00', 'HH:mm:ss'),
            }}
          />
        </Form.Item>
        <Form.Item name="endDate" label="End Date">
          <DatePicker
            allowClear
            showTime={{
              defaultValue: moment('00:00', 'HH:mm:ss'),
            }}
            format={'YYYY-MM-DD HH:mm:ss'}
          />
        </Form.Item>
        <Form.Item name="startFillDate" label="Start Fill Date">
          <DatePicker
            allowClear
            format={'YYYY-MM-DD HH:mm:ss'}
            showTime={{
              defaultValue: moment('00:00', 'HH:mm:ss'),
            }}
          />
        </Form.Item>
        <Form.Item name="endFillDate" label="End Fill Date">
          <DatePicker
            allowClear
            showTime={{
              defaultValue: moment('00:00', 'HH:mm:ss'),
            }}
            format={'YYYY-MM-DD HH:mm:ss'}
          />
        </Form.Item>
        {(['EXPIRE', 'PAST'].includes(activeTab) || isExport) && (
          <>
            <Form.Item name="startSettlementDate" label="Start Settlement Date">
              <DatePicker
                allowClear
                format={'YYYY-MM-DD HH:mm:ss'}
                showTime={{
                  defaultValue: moment('00:00', 'HH:mm:ss'),
                }}
              />
            </Form.Item>
            <Form.Item name="endSettlementDate" label="End Settlement Date">
              <DatePicker
                allowClear
                showTime={{
                  defaultValue: moment('00:00', 'HH:mm:ss'),
                }}
                format={'YYYY-MM-DD HH:mm:ss'}
              />
            </Form.Item>
          </>
        )}
        {(['EXPIRE'].includes(activeTab) || isExport) && (
          <>
            <Form.Item name="startExpiryDate" label="Start Expiry Date">
              <DatePicker
                allowClear
                format={'YYYY-MM-DD HH:mm:ss'}
                showTime={{
                  defaultValue: moment('00:00', 'HH:mm:ss'),
                }}
              />
            </Form.Item>
            <Form.Item name="endExpiryDate" label="End Expiry Date">
              <DatePicker
                allowClear
                showTime={{
                  defaultValue: moment('00:00', 'HH:mm:ss'),
                }}
                format={'YYYY-MM-DD HH:mm:ss'}
              />
            </Form.Item>
          </>
        )}
      </Section>

      <Section id="flags" title="Flags">
        <Form.Item
          tooltip="If placed is true, only orders that have been placed on the broker will be exported"
          label="Placed"
          name="placed">
          <Select placeholder="Select option">
            <Select.Option value={true}>YES</Select.Option>
            <Select.Option value={false}>NO</Select.Option>
            <Select.Option value={null}>EITHER</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          tooltip="If bundled is true, orders will be exported in a bundled format"
          label="Bundle"
          name="bundle">
          <Select placeholder="Select option">
            <Select.Option value={true}>YES</Select.Option>
            <Select.Option value={false}>NO</Select.Option>
            <Select.Option value={null}>EITHER</Select.Option>
          </Select>
        </Form.Item>
        <div className={theme.filterSwitches}>
          <Form.Item
            name="assetPriceOverFloated"
            tooltip="Best used with market orders: compares the instrument's latest price against the limit price × 1.03."
            label="Asset Price Over Floated"
            valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="collection"
            label="Collection Orders"
            valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="affiliateOrder"
            label="Affiliate Orders"
            valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="hold"
            label="Orders on Hold"
            valuePropName="checked"
            tooltip="Orders on hold are left out by default; this asks for them explicitly.">
            <Switch />
          </Form.Item>
          <Form.Item
            name="settledBySystem"
            label="Settled by System"
            tooltip="Settled automatically rather than by an admin."
            valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="includePendingOrders"
            label="Include Pending"
            tooltip="Adds pending orders to whatever this queue already matches."
            valuePropName="checked">
            <Switch />
          </Form.Item>
        </div>
      </Section>
    </>
  );

  if (isExport)
    return (
      <div className={theme.filterFieldsFlat}>
        {Fields}
        {/* Both are read by the broker export and have never had a control,
            so every export so far has sent them undefined. */}
        {/* Eight fields are documented as export-only. They do nothing on a
            queue request, so they are collected here and nowhere else. */}
        <Section id="export" title="Export only">
          <Form.Item name="brokerId" label="Broker">
            <BrokerSelect skip={optionsSkip} />
          </Form.Item>
          <Form.Item
            name="brokerIds"
            label="Brokers"
            tooltip="Export orders belonging to any of these brokers.">
            <BrokerSelect mode="multiple" skip={optionsSkip} />
          </Form.Item>
          <Form.Item
            name="statementUrl"
            label="Statement URL"
            tooltip="Back office exports only. Overrides the statement endpoint on the selected broker.">
            <Input allowClear placeholder="Defaults to the broker's own" />
          </Form.Item>
          <Form.Item
            name="historyUrl"
            label="History URL"
            tooltip="Back office exports only. Overrides the history endpoint on the selected broker.">
            <Input allowClear placeholder="Defaults to the broker's own" />
          </Form.Item>
          <Form.Item
            name="key"
            label="Export Key"
            tooltip="An external correlation id, echoed back verbatim, so this export can be tracked without waiting on the email.">
            <Input allowClear placeholder="Your own reference for this run" />
          </Form.Item>
          <Form.Item
            name="backoffice"
            label="Back Office"
            valuePropName="checked"
            tooltip="Informal orders processed by automated back office, which carry no fees.">
            <Switch />
          </Form.Item>
        </Section>
      </div>
    );

  const sectionCounts = SECTIONS.map(section => ({
    ...section,
    count: section.fields.filter(field => isSet(values[field])).length,
  }));
  const totalSet = sectionCounts.reduce(
    (total, section) => total + section.count,
    0,
  );

  return (
    <div className={theme.filterModal}>
      <div className={theme.filterHead}>
        <div>
          <h2>Filter orders</h2>
          <p>
            {totalSet
              ? `${totalSet} field${
                  totalSet === 1 ? '' : 's'
                } set. Applying replaces the filters in use.`
              : 'Narrow this queue. Applying replaces the filters in use.'}
          </p>
        </div>
      </div>
      <Form
        onFinish={submitted => {
          handleFilter(submitted);
          reset();
        }}
        onValuesChange={(_, all) => setValues(all)}
        form={form}
        layout="vertical"
        className={theme.filterForm}>
        <div className={theme.filterBody}>
          <nav className={theme.filterNav} aria-label="Filter sections">
            {sectionCounts.map(section => (
              <button
                type="button"
                key={section.id}
                aria-current={activeSection === section.id}
                className={`${theme.filterNavItem} ${
                  activeSection === section.id ? theme.filterNavItemOn : ''
                }`}
                onClick={() => goToSection(section.id)}>
                {section.title}
                {section.count > 0 && (
                  <span className={theme.filterNavCount}>{section.count}</span>
                )}
              </button>
            ))}
          </nav>
          <div
            className={theme.filterPanes}
            ref={panesRef}
            onScroll={handlePanesScroll}>
            {Fields}
          </div>
        </div>
        <div className={theme.filterFoot}>
          <button
            type="button"
            className={theme.filterReset}
            disabled={!totalSet}
            onClick={() => {
              form.resetFields();
              setValues({});
            }}>
            Reset fields
          </button>
          {/* Not disabled while the queue refetches: that blocked the whole
              panel on a request the operator had already moved on from. A
              field still loading its own options disables itself instead. */}
          <SubmitButton label="Apply Changes" />
        </div>
      </Form>
    </div>
  );
};
